// @ts-nocheck
// Request deduplication and short-lived caching layer
// Prevents the N+1 API call problem during page loads

interface CacheEntry<T = any> {
  data: T
  timestamp: number
  promise?: Promise<T>
}

interface PendingRequest<T = any> {
  promise: Promise<T>
  resolve: (value: T) => void
  reject: (reason?: any) => void
}

class RequestCache {
  private cache = new Map<string, CacheEntry>()
  private pendingRequests = new Map<string, PendingRequest>()
  private readonly TTL = 3000 // 3 seconds cache TTL

  // Streaming endpoints that should never be cached
  private readonly NON_CACHEABLE_PATTERNS = [
    '/stream',
    '/chat',
    '/orchestrate',
  ]

  private isCacheable(url: string, method: string): boolean {
    if (method !== 'GET')
      return false
    return !this.NON_CACHEABLE_PATTERNS.some(pattern => url.includes(pattern))
  }

  private generateKey(url: string, method: string, body?: any): string {
    const bodyStr = body ? JSON.stringify(body) : ''
    return `${method}:${url}:${bodyStr}`
  }

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > this.TTL
  }

  private cleanupExpired(): void {
    const now = Date.now()
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.TTL) {
        this.cache.delete(key)
      }
    }
  }

  // Main request wrapper
  async request<T>(
    url: string,
    options: RequestInit = {},
  ): Promise<T> {
    const method = options.method || 'GET'
    const cacheKey = this.generateKey(url, method, options.body)

    // Clean up expired entries periodically
    if (Math.random() < 0.1) { // 10% chance on each request
      this.cleanupExpired()
    }

    // For non-cacheable requests, just fetch directly
    if (!this.isCacheable(url, method)) {
      return this.fetchDirectly<T>(url, options)
    }

    // Check if we have a valid cached response
    const cached = this.cache.get(cacheKey)
    if (cached && !this.isExpired(cached)) {
      console.log(`🎯 Cache hit: ${method} ${url}`)
      return cached.data
    }

    // Check if there's already a request in flight
    const pending = this.pendingRequests.get(cacheKey)
    if (pending) {
      console.log(`⏳ Deduplicating: ${method} ${url}`)
      return pending.promise
    }

    // Create new request
    console.log(`🌐 Cache miss: ${method} ${url}`)
    return this.createRequest<T>(cacheKey, url, options)
  }

  private async createRequest<T>(
    cacheKey: string,
    url: string,
    options: RequestInit,
  ): Promise<T> {
    let resolve: (value: T) => void
    let reject: (reason?: any) => void

    const promise = new Promise<T>((res, rej) => {
      resolve = res
      reject = rej
    })

    this.pendingRequests.set(cacheKey, { promise, resolve: resolve!, reject: reject! })

    try {
      const data = await this.fetchDirectly<T>(url, options)

      // Cache the result
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      })

      resolve!(data)
      return data
    }
    catch (error) {
      reject!(error)
      throw error
    }
    finally {
      this.pendingRequests.delete(cacheKey)
    }
  }

  private async fetchDirectly<T>(url: string, options: RequestInit): Promise<T> {
    const response = await fetch(url, options)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    return response.json()
  }

  // Clear cache for specific patterns (useful for invalidation)
  invalidatePattern(pattern: string): void {
    for (const [key] of this.cache.entries()) {
      if (key.includes(pattern)) {
        this.cache.delete(key)
        console.log(`♻️ Invalidated cache: ${key}`)
      }
    }
  }

  // Clear all cache
  clear(): void {
    this.cache.clear()
    this.pendingRequests.clear()
    console.log('🧹 Cache cleared')
  }

  // Get cache stats (for debugging)
  getStats(): { size: number, pending: number, keys: string[] } {
    return {
      size: this.cache.size,
      pending: this.pendingRequests.size,
      keys: Array.from(this.cache.keys()),
    }
  }
}

// Global cache instance
export const requestCache = new RequestCache()

// Wrapper for fetch calls that need caching
export async function cachedFetch<T = any>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  return requestCache.request<T>(url, options)
}

// Helper to invalidate cache when data changes
export function invalidateCache(pattern: string): void {
  requestCache.invalidatePattern(pattern)
}
